// src/pages/employer/ApplyFilterEditor.jsx
// Editor for a single Apply Filter — name, intro, and ordered questions.
// Filters are standalone; their association with positions happens later via
// the application gateway configuration.

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { applyFilterAPI } from '../../services/api';
import { Modal, useConfirm } from '../../components/shared/UI';
import {
  ArrowLeft, Plus, Trash2, ChevronUp, ChevronDown, Pencil, Save,
} from 'lucide-react';

const TYPE_META = [
  { type: 'TEXTBOX',      label: 'Text box',     hint: 'Single-line short answer'                },
  { type: 'TEXTAREA',     label: 'Text area',    hint: 'Multi-line long answer'                  },
  { type: 'CHECKBOX',     label: 'Checkboxes',   hint: 'Applicant picks any number of options'   },
  { type: 'RADIO',        label: 'Radio buttons',hint: 'Applicant picks exactly one option'      },
  { type: 'DROPDOWN',     label: 'Drop down',    hint: 'Applicant picks one from a list'         },
  { type: 'DATE',         label: 'Date',         hint: 'Applicant picks a date'                  },
  { type: 'LINEAR_SCALE', label: 'Linear scale', hint: 'Applicant rates on a numeric scale'      },
  { type: 'FILE_UPLOAD',  label: 'File upload',  hint: 'Applicant uploads a file (not scorable)' },
];
const TYPE_LABEL = Object.fromEntries(TYPE_META.map(t => [t.type, t.label]));

const newId = () => crypto.randomUUID();
const blankQuestion = (type) => ({
  id: newId(),
  order: 0,
  type,
  prompt: '',
  required: false,
  config: defaultConfig(type),
  correctAnswer: null,
});

function defaultConfig(type) {
  switch (type) {
    case 'CHECKBOX':
    case 'RADIO':
    case 'DROPDOWN':
      return { options: [] };
    case 'LINEAR_SCALE':
      return { min: 1, max: 5, minLabel: '', maxLabel: '' };
    case 'FILE_UPLOAD':
      return { acceptedMimeTypes: '.pdf,.doc,.docx' };
    default:
      return null;
  }
}

const AnswerPreview = ({ q }) => {
  const { type, correctAnswer, config } = q;
  if (!correctAnswer && correctAnswer !== 0) return <span style={{ color: '#999', fontStyle: 'italic' }}>No correct answer set</span>;
  if (type === 'CHECKBOX' && Array.isArray(correctAnswer)) return correctAnswer.join(', ');
  if (type === 'LINEAR_SCALE' && typeof correctAnswer === 'object') {
    return `${correctAnswer.min ?? ''}–${correctAnswer.max ?? ''} (of ${config?.min}–${config?.max})`;
  }
  return String(correctAnswer);
};

const OptionsEditor = ({ options, onChange }) => {
  const update = (i, v) => { const next = [...options]; next[i] = v; onChange(next); };
  const remove = (i)    => onChange(options.filter((_, idx) => idx !== i));
  const add    = ()     => onChange([...options, '']);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {options.map((opt, i) => (
        <div key={i} style={{ display: 'flex', gap: 6 }}>
          <input className="form-input" value={opt} onChange={(e) => update(i, e.target.value)} placeholder={`Option ${i + 1}`} />
          <button type="button" className="btn btn-danger btn-sm" onClick={() => remove(i)}>×</button>
        </div>
      ))}
      <button type="button" className="btn btn-outline btn-sm" onClick={add}
        style={{ alignSelf: 'flex-start' }}>
        <Plus size={13} style={{ marginRight: 4 }} /> Add option
      </button>
    </div>
  );
};

const CorrectAnswerEditor = ({ q, onChange }) => {
  const { type, config, correctAnswer } = q;
  const setCA = (val) => onChange(val);

  if (type === 'FILE_UPLOAD') {
    return <p style={{ fontSize: 12, color: '#777', margin: 0 }}>File uploads are not scored; applicants' uploads are collected for review only.</p>;
  }
  if (type === 'TEXTBOX' || type === 'TEXTAREA') {
    const Tag = type === 'TEXTAREA' ? 'textarea' : 'input';
    return (
      <Tag className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}
        rows={type === 'TEXTAREA' ? 3 : undefined}
        placeholder="Leave blank for no scoring" />
    );
  }
  if (type === 'DATE') {
    return (
      <input className="form-input" type="date" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)} />
    );
  }
  if (type === 'RADIO' || type === 'DROPDOWN') {
    const options = config?.options || [];
    return (
      <select className="form-input" value={correctAnswer ?? ''}
        onChange={(e) => setCA(e.target.value || null)}>
        <option value="">— No correct answer —</option>
        {options.map((o, i) => <option key={i} value={o}>{o || `Option ${i + 1}`}</option>)}
      </select>
    );
  }
  if (type === 'CHECKBOX') {
    const options = config?.options || [];
    const selected = Array.isArray(correctAnswer) ? correctAnswer : [];
    const toggle = (opt) => {
      const next = selected.includes(opt) ? selected.filter(o => o !== opt) : [...selected, opt];
      setCA(next.length ? next : null);
    };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p style={{ fontSize: 12, color: '#666', margin: '0 0 4px 0' }}>Pick the option set that counts as correct.</p>
        {options.map((opt, i) => (
          <label key={i} style={{ fontSize: 13 }}>
            <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} /> {opt || `Option ${i + 1}`}
          </label>
        ))}
      </div>
    );
  }
  if (type === 'LINEAR_SCALE') {
    const ca = (correctAnswer && typeof correctAnswer === 'object') ? correctAnswer : { min: '', max: '' };
    return (
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#666' }}>Correct if answer is between</span>
        <input className="form-input" type="number" style={{ width: 70 }}
          value={ca.min ?? ''}
          onChange={(e) => setCA({ ...ca, min: e.target.value === '' ? null : Number(e.target.value) })} />
        <span>and</span>
        <input className="form-input" type="number" style={{ width: 70 }}
          value={ca.max ?? ''}
          onChange={(e) => setCA({ ...ca, max: e.target.value === '' ? null : Number(e.target.value) })} />
        <button type="button" className="btn btn-outline btn-sm" onClick={() => setCA(null)}>Clear</button>
      </div>
    );
  }
  return null;
};

const TypeConfigEditor = ({ q, onConfigChange }) => {
  const { type, config } = q;
  if (type === 'CHECKBOX' || type === 'RADIO' || type === 'DROPDOWN') {
    return (
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Options</label>
        <OptionsEditor
          options={config?.options || []}
          onChange={(options) => onConfigChange({ ...(config || {}), options })}
        />
      </div>
    );
  }
  if (type === 'LINEAR_SCALE') {
    const cfg = config || { min: 1, max: 5, minLabel: '', maxLabel: '' };
    return (
      <div style={{ marginBottom: 14, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label className="form-label">Scale min</label>
          <input className="form-input" type="number" value={cfg.min ?? 1}
            onChange={(e) => onConfigChange({ ...cfg, min: Number(e.target.value) })} />
        </div>
        <div>
          <label className="form-label">Scale max</label>
          <input className="form-input" type="number" value={cfg.max ?? 5}
            onChange={(e) => onConfigChange({ ...cfg, max: Number(e.target.value) })} />
        </div>
        <div>
          <label className="form-label">Min label</label>
          <input className="form-input" value={cfg.minLabel ?? ''}
            onChange={(e) => onConfigChange({ ...cfg, minLabel: e.target.value })}
            placeholder="e.g. Poor" />
        </div>
        <div>
          <label className="form-label">Max label</label>
          <input className="form-input" value={cfg.maxLabel ?? ''}
            onChange={(e) => onConfigChange({ ...cfg, maxLabel: e.target.value })}
            placeholder="e.g. Excellent" />
        </div>
      </div>
    );
  }
  if (type === 'FILE_UPLOAD') {
    const cfg = config || { acceptedMimeTypes: '' };
    return (
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Accepted file types</label>
        <input className="form-input" value={cfg.acceptedMimeTypes ?? ''}
          onChange={(e) => onConfigChange({ ...cfg, acceptedMimeTypes: e.target.value })}
          placeholder=".pdf, .doc, .docx" />
      </div>
    );
  }
  return null;
};

const QuestionModal = ({ open, initial, onClose, onSave }) => {
  const [q, setQ] = useState(initial || blankQuestion('TEXTBOX'));
  useEffect(() => { if (open) setQ(initial || blankQuestion('TEXTBOX')); }, [open, initial]);
  if (!open) return null;

  const canSave = q.prompt.trim().length > 0;
  const update = (patch) => setQ(prev => ({ ...prev, ...patch }));
  const changeType = (newType) => {
    setQ(prev => ({
      ...prev,
      type: newType,
      config: defaultConfig(newType),
      correctAnswer: null,
    }));
  };

  return (
    <Modal open={open} onClose={onClose} title="Filter question" width="620px"
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={() => onSave(q)}>Save question</button>
        </div>
      }>
      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Question type</label>
        <select className="form-input" value={q.type} onChange={(e) => changeType(e.target.value)}>
          {TYPE_META.map(t => <option key={t.type} value={t.type}>{t.label}</option>)}
        </select>
      </div>

      <div style={{ marginBottom: 14 }}>
        <label className="form-label">Prompt</label>
        <textarea className="form-input" rows={2} value={q.prompt}
          onChange={(e) => update({ prompt: e.target.value })}
          placeholder="Write the question exactly as applicants will see it" />
      </div>

      <TypeConfigEditor q={q} onConfigChange={(config) => update({ config })} />

      <div style={{ marginBottom: 14 }}>
        <label style={{ fontSize: 13 }}>
          <input type="checkbox" checked={!!q.required} onChange={(e) => update({ required: e.target.checked })} />
          {' '}Mandatory — applicants cannot submit without answering.
        </label>
      </div>

      <div style={{ marginBottom: 4 }}>
        <label className="form-label">Correct answer (optional)</label>
        <CorrectAnswerEditor q={q} onChange={(correctAnswer) => update({ correctAnswer })} />
      </div>
    </Modal>
  );
};

export const ApplyFilterEditor = () => {
  const { filterId } = useParams();
  const navigate = useNavigate();
  const { activeOrg, activeProfile } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [name, setName]           = useState('');
  const [intro, setIntro]         = useState('');
  const [questions, setQuestions] = useState([]);
  const [modalQ, setModalQ]       = useState(null);
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  useEffect(() => {
    if (!activeOrg || !activeProfile) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await applyFilterAPI.get(activeOrg.id, activeProfile.id, filterId);
        if (!alive) return;
        const filter = data?.data?.filter;
        if (!filter) {
          toast.error('Apply filter not found');
          navigate('/employer/libraries/apply-filters', { replace: true });
          return;
        }
        setName(filter.name || '');
        setIntro(filter.intro || '');
        setQuestions(Array.isArray(filter.questions) ? filter.questions : []);
      } catch {
        // interceptor
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [activeOrg, activeProfile, filterId, navigate]);

  const addQuestion    = (type) => { setTypePickerOpen(false); setModalQ({ ...blankQuestion(type), __isNew: true }); };
  const editQuestion   = (q)    => setModalQ({ ...q });
  const removeQuestion = async (id) => {
    if (!await confirm('Remove question', 'This will remove it from the filter. Save the filter to persist.')) return;
    setQuestions(prev => prev.filter(q => q.id !== id));
  };
  const moveQuestion = (id, dir) => {
    setQuestions(prev => {
      const idx = prev.findIndex(q => q.id === id);
      if (idx < 0) return prev;
      const swap = idx + dir;
      if (swap < 0 || swap >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[swap]] = [next[swap], next[idx]];
      return next.map((q, i) => ({ ...q, order: i }));
    });
  };

  const handleSaveQuestion = (q) => {
    const isNew = !!q.__isNew;
    const clean = { ...q }; delete clean.__isNew;
    setQuestions(prev => {
      if (isNew) return [...prev, { ...clean, order: prev.length }];
      return prev.map(x => x.id === q.id ? clean : x);
    });
    setModalQ(null);
  };

  const saveFilter = async () => {
    const cleanName = name.trim();
    if (!cleanName) {
      toast.error('Filter name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: cleanName,
        intro: intro || null,
        questions: questions.map((q, i) => ({
          order:         i,
          type:          q.type,
          prompt:        q.prompt,
          required:      !!q.required,
          config:        q.config ?? null,
          correctAnswer: q.correctAnswer ?? null,
        })),
      };
      const { data } = await applyFilterAPI.update(activeOrg.id, activeProfile.id, filterId, payload);
      const fresh = data?.data?.filter;
      if (fresh) {
        setName(fresh.name || '');
        setIntro(fresh.intro || '');
        setQuestions(Array.isArray(fresh.questions) ? fresh.questions : []);
      }
      toast.success('Apply filter saved');
    } catch {
      // interceptor
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFilter = async () => {
    if (!await confirm('Delete apply filter', 'This permanently removes the filter and its questions.')) return;
    try {
      await applyFilterAPI.delete(activeOrg.id, activeProfile.id, filterId);
      toast.success('Apply filter deleted');
      navigate('/employer/libraries/apply-filters', { replace: true });
    } catch {
      // interceptor
    }
  };

  return (
    <div className="page-content-wide" style={{ padding: '16px 20px 40px' }}>
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <button onClick={() => navigate('/employer/libraries/apply-filters')}
          className="btn btn-secondary btn-sm" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <ArrowLeft size={14} /> Back to library
        </button>
        <span style={{ flex: 1, fontSize: 16, fontWeight: 600 }}>
          Apply Filter
        </span>
        <button className="btn btn-danger btn-sm" onClick={handleDeleteFilter} disabled={saving || loading}>
          <Trash2 size={13} style={{ marginRight: 4 }} /> Delete
        </button>
        <button className="btn btn-success" onClick={saveFilter} disabled={saving || loading}>
          <Save size={13} style={{ marginRight: 6 }} /> {saving ? 'Saving…' : 'Save filter'}
        </button>
      </div>

      {loading ? (
        <p style={{ color: '#888', padding: 20 }}>Loading…</p>
      ) : (
        <div style={{ padding: '18px 0', maxWidth: 820 }}>
          <div style={{ background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: 16, marginBottom: 16 }}>
            <label className="form-label">Filter name</label>
            <input className="form-input" value={name} onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Right-to-work + driving licence" />
            <label className="form-label" style={{ marginTop: 12 }}>Intro (shown above the questions)</label>
            <textarea className="form-input" rows={3} value={intro} onChange={(e) => setIntro(e.target.value)}
              placeholder="Optional. e.g. Please answer the following short questions so we can shortlist your application." />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0e4e95' }}>
              Questions ({questions.length})
            </h3>
            <button className="btn btn-primary btn-sm" onClick={() => setTypePickerOpen(true)}>
              <Plus size={13} style={{ marginRight: 4 }} /> Add question
            </button>
          </div>

          {questions.length === 0 ? (
            <div style={{ background: 'white', border: '1px dashed var(--border)', borderRadius: 6, padding: 20, color: '#888', fontStyle: 'italic' }}>
              No questions yet. Add your first one.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {questions.map((q, i) => (
                <div key={q.id} style={{
                  background: 'white', border: '1px solid var(--border)', borderRadius: 6, padding: 14,
                }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 11, color: '#3D80CC', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                          {i + 1}. {TYPE_LABEL[q.type] || q.type}
                        </span>
                        {q.required && <span style={{ fontSize: 11, color: '#CC0000' }}>(required)</span>}
                      </div>
                      <div style={{ fontSize: 14, color: '#1a1a1a', marginBottom: 4 }}>{q.prompt || <em style={{ color: '#999' }}>No prompt</em>}</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        <strong>Correct answer: </strong><AnswerPreview q={q} />
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button className="btn btn-outline btn-sm" onClick={() => moveQuestion(q.id, -1)}
                        disabled={i === 0} title="Move up">
                        <ChevronUp size={13} />
                      </button>
                      <button className="btn btn-outline btn-sm" onClick={() => moveQuestion(q.id, +1)}
                        disabled={i === questions.length - 1} title="Move down">
                        <ChevronDown size={13} />
                      </button>
                    </div>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 10, borderTop: '1px solid #eee', paddingTop: 8 }}>
                    <button className="btn btn-outline btn-sm" onClick={() => editQuestion(q)}>
                      <Pencil size={13} style={{ marginRight: 4 }} /> Edit
                    </button>
                    <button className="btn btn-danger btn-sm" onClick={() => removeQuestion(q.id)}>
                      <Trash2 size={13} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      <Modal open={typePickerOpen} onClose={() => setTypePickerOpen(false)} title="Pick a question type" width="460px">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {TYPE_META.map(t => (
            <button key={t.type} className="btn btn-outline"
              style={{ justifyContent: 'flex-start', textAlign: 'left' }}
              onClick={() => addQuestion(t.type)}>
              <span style={{ fontWeight: 700 }}>{t.label}</span>
              <span style={{ marginLeft: 8, color: '#777', fontWeight: 400 }}>— {t.hint}</span>
            </button>
          ))}
        </div>
      </Modal>

      <QuestionModal open={!!modalQ} initial={modalQ} onClose={() => setModalQ(null)} onSave={handleSaveQuestion} />
      {dialog}
    </div>
  );
};
